import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { AlertTriangle, AlertCircle, Clock, ChevronRight } from "lucide-react";
import { SEVERITY_STYLES, ALERT_CATEGORIES } from "@/components/vendor/alerts";

const CATEGORY_BY_KEY = Object.fromEntries(ALERT_CATEGORIES.map((c) => [c.key, c]));

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const daysLabel = (n) => {
  if (n === null || n === undefined) return "";
  if (n < 0) return `${Math.abs(n)}d overdue`;
  if (n === 0) return "today";
  return `in ${n}d`;
};

// Compact "needs attention" panel shown above the My Cars grid. Fetches the digest from
// /api/vendor/alerts; collapses to nothing when there's no issue across the fleet.
export default function VendorAlertsSummary({ onJumpToCar }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/alerts");
      setData(data);
    } catch (e) {
      console.error("alerts digest load failed:", e);
    }
  }, []);

  useEffect(() => {
    load();
    // Re-fetch every 5 min so the severity refreshes as days pass on long-lived sessions.
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [load]);

  if (!data || data.items.length === 0) return null;
  const { counts, cars_needing_attention: cars, total_cars: total, items } = data;
  // Show up to 4 most-urgent items as inline chips, with a "+N more" if exceeded.
  const top = items.slice(0, 4);
  const remaining = items.length - top.length;

  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 mb-6" data-testid="vendor-alerts-summary">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-lg font-medium" data-testid="vendor-alerts-summary-title">
              {cars} of {total} car{total === 1 ? "" : "s"} need{cars === 1 ? "s" : ""} attention
            </div>
            <div className="text-xs text-neutral-600 mt-0.5 flex items-center gap-2 flex-wrap">
              {counts.expired > 0 && (
                <span className="inline-flex items-center gap-1 text-rose-700" data-testid="vendor-alerts-count-expired">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                  {counts.expired} expired
                </span>
              )}
              {counts.critical > 0 && (
                <span className="inline-flex items-center gap-1 text-orange-700" data-testid="vendor-alerts-count-critical">
                  <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                  {counts.critical} expiring this week
                </span>
              )}
              {counts.warning > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-800" data-testid="vendor-alerts-count-warning">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  {counts.warning} expiring this month
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {top.map((it, i) => {
          const cat = CATEGORY_BY_KEY[it.category];
          const Icon = cat?.icon || AlertCircle;
          const s = SEVERITY_STYLES[it.severity];
          return (
            <li
              key={`${it.car_id}-${it.category}`}
              className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-border text-sm hover:border-neutral-400 transition cursor-pointer"
              onClick={() => onJumpToCar?.(it.car_id)}
              data-testid={`vendor-alerts-summary-item-${i}`}
            >
              <Icon className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-900 text-sm truncate">
                  {it.car_make} {it.car_model} <span className="text-neutral-400 font-normal">·</span> {it.label}
                </div>
                {it.note && <div className="text-xs text-neutral-500 truncate">{it.note}</div>}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.bg} ${s.text} shrink-0`}>
                <Clock className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5" />
                {formatDate(it.date)} · {daysLabel(it.days_until)}
              </span>
              <ChevronRight className="h-3.5 w-3.5 text-neutral-300 shrink-0" />
            </li>
          );
        })}
        {remaining > 0 && (
          <li className="text-xs text-neutral-500 pl-3" data-testid="vendor-alerts-summary-more">
            +{remaining} more — open a car to review
          </li>
        )}
      </ul>
    </div>
  );
}
